import { stripHtml } from '@ampcore/string-format'
import { getEnvironment } from '../../common/environment'
import { ResourceCardProps } from './ResourceCard'
import xss from 'xss'

interface MirrorMeta {
  path: string
}

interface BlockMeta {
  type: string
  mirror?: MirrorMeta
}

interface ContentBlock {
  _meta: BlockMeta
  content: string | { content: string }
  columns?: {
    left: { content: string }[]
    right: { content: string }[]
  }
  left?: { content: string }
  middle?: { content: string }
  right?: { content: string }
  innerCompound?: {
    heading: string
    table?: { content: { content: string } }
  }[]
  blocks?: {
    innerCompound: {
      heading: string
      table?: { content: { content: string } }
    }[]
  }
  quote?: { content: string }
  person?: { content: string }
  graybox?: { content: string }[]
  image?: { _meta?: { mirror?: MirrorMeta } }
}

interface ContentItem {
  blocks?: ContentBlock[]
  DetailContentCompound?: ContentItem[]
}

interface ImageObject {
  _meta?: { mirror?: MirrorMeta }
}

interface WatsonResult {
  url?: string
  title?: string
  subtopic?: string
  subTopicDetails?: string
  description?: string
  imageUrl?: string
  text?: string
}

interface CMSResult {
  _meta: { id: string; path: string }
  _links: { site: { href: string } }
  title: string
  subTopic?: string[]
  subtopicdetails?: string[]
  MetadataCompound?: { omniDescription?: string }
  DetailContentCompound?: ContentItem[]
  content?: { content: string }
  heroesImage?: { image?: ImageObject }
  banner?: { image?: ImageObject }
}

const decodeHTML = (html: string): string => {
  const txt = document.createElement('textarea')
  txt.innerHTML = xss(html)
  return txt.value
}

const computeReadTime = (text: string): string => {
  const strippedText = stripHtml(text)
  const wpm = 225
  const words = strippedText.trim().split(/\s+/).length
  const time = Math.ceil(words / wpm)
  return time === 1 ? '1 min read' : time + ' min read'
}

const pullContent = (item: ContentItem): string => {
  let content = ''
  if (!item.blocks || item.blocks.length === 0) return content

  const block = item.blocks[0]
  switch (block._meta.type) {
  case 'hippostd:html':
    content += typeof block.content === 'string' ? block.content : ''
    break
  case 'ampcom:TwoColumnCompound':
    content +=
        (block.columns?.left[0].content ?? '') +
        (block.columns?.right[0].content ?? '')
    break
  case 'ampcom:ThreeColumnCompound':
    content +=
        (block.left?.content ?? '') +
        (block.middle?.content ?? '') +
        (block.right?.content ?? '')
    break
  case 'ampcom:AccordionCompound':
    if (block.innerCompound) {
      block.innerCompound.forEach(obj => {
        content += obj.heading + (obj.table?.content.content ?? '')
      })
    } else if (block.blocks?.innerCompound) {
      block.blocks.innerCompound.forEach(obj => {
        content += obj.heading + (obj.table?.content.content ?? '')
      })
    }
    break
  case 'ampcom:QuoteCompound':
    content += (block.quote?.content ?? '') + (block.person?.content ?? '')
    break
  case 'ampcom:CalloutCompound':
    if (block.graybox) {
      content += block.graybox[0].content
    } else if (Array.isArray(block.content)) {
      content += block.content[0]
    }
    break
  case 'ampcom:Article':
    if (item.DetailContentCompound) {
      item.DetailContentCompound.forEach(subitem => {
        if (subitem.blocks) {
          content += pullContent(subitem)
        }
      })
    }
    break
  case 'ampcom:ImageCompound':
  case 'ampcom:SocialShareCompound':
  case 'ampcom:VideoCompound':
  case 'ampcom:LinkCompound':
  case 'ampcom:PromoCompound':
  case 'ampcom:TabsSection':
    break
  default: {
    const defaultContent = block.content
    if (typeof defaultContent === 'object' && defaultContent !== null) {
      content += defaultContent.content
    }
  }
  }
  return content
}

const getTimeToReadFromDoc = (doc: CMSResult): string => {
  let newContent = ''
  try {
    if (doc.DetailContentCompound) {
      doc.DetailContentCompound.forEach(item => {
        if (item.blocks) {
          newContent = pullContent(item)
        }
      })
    } else if (doc.content) {
      newContent = doc.content.content
    }
  } catch {
    console.error('Failed to calculate read time from doc')
  }
  return computeReadTime(newContent)
}

const getCompoundImage = (result: CMSResult): string => {
  let imagePath = ''
  try {
    if (result.DetailContentCompound) {
      const filteredContent = result.DetailContentCompound.find(item => {
        if (item.blocks && item.blocks.length > 0) {
          return item.blocks[0]._meta.type === 'ampcom:ImageCompound'
        }
        return false
      })
      if (filteredContent?.blocks?.[0]?.image?._meta?.mirror?.path) {
        imagePath = filteredContent.blocks[0].image._meta.mirror.path
      }
    }
  } catch {
    console.error('Failed to get image component')
  }
  return imagePath
}

const getCardImageForCMS = (result: CMSResult): string => {
  const env = getEnvironment()
  const domainBasedPrefix = env === 'localhost' ? '/site/ampcom' : ''
  const defaultImagePath =
    'content/gallery/ampcom/bg-resource-center-default.png'
  const compoundImagePath = getCompoundImage(result)

  const getImagePath = (imageObj: ImageObject | undefined) => {
    return !!imageObj?._meta?.mirror?.path && imageObj._meta.mirror.path !== ''
  }

  if (getImagePath(result.heroesImage?.image)) {
    return `${domainBasedPrefix}/binaries/${result.heroesImage?.image?._meta?.mirror?.path}`
  } else if (getImagePath(result.banner?.image)) {
    return `${domainBasedPrefix}/binaries/${result.banner?.image?._meta?.mirror?.path}`
  } else if (compoundImagePath !== '') {
    return `${domainBasedPrefix}/binaries/${compoundImagePath}`
  }
  return `${domainBasedPrefix}/binaries/${defaultImagePath}`
}

export function mapWatsonResultToCardProps(
  result: WatsonResult
): ResourceCardProps {
  const url = result.url ?? ''
  const title = result.title ?? ''
  const subtopic = result.subtopic ?? ''
  const subtopicdetails = result.subTopicDetails ?? ''
  const description = result.description ?? ''
  const imageUrl = result.imageUrl ?? ''
  const text = result.text ?? ''

  const cardImage =
    imageUrl === ''
      ? `${window.AMPCOMPATH}images/dac-missing-image.png`
      : imageUrl

  return {
    url,
    title,
    subtopics: subtopic ? subtopic.split(',').map(item => item.trim()) : [],
    subtopicdetails: subtopicdetails
      ? subtopicdetails.split(',').map(item => item.trim())
      : [],
    description: decodeHTML(description),
    imageUrl: cardImage,
    readTime: computeReadTime(text)
  }
}

export function mapCMSResultToCardProps(result: CMSResult): ResourceCardProps {
  const subtopics = result.subTopic ?? []
  const subtopicdetails = result.subtopicdetails ?? []
  const description = result.MetadataCompound?.omniDescription
    ? decodeHTML(result.MetadataCompound.omniDescription)
    : ''

  return {
    url: result._links?.site?.href ?? '',
    title: result.title ?? '',
    subtopics,
    subtopicdetails,
    description,
    imageUrl: getCardImageForCMS(result),
    readTime: getTimeToReadFromDoc(result)
  }
}
